import { useState } from 'react'
import React from 'react'
import { Tooltip, type TooltipProps } from 'antd'

/**
 * Drop-in replacement for Ant Design Tooltip that dismisses on click.
 * Prevents the sticky-tooltip bug on mobile where a tap triggers a synthetic
 * mouseenter, the tooltip appears, but there's no mouseleave to close it.
 *
 * Must use forwardRef so that @rc-component/dropdown's supportRef() check
 * passes — without it, Dropdown/Popconfirm can't get a DOM ref to this
 * component, causing popups to appear at top-left and immediately close.
 */
export const DismissibleTooltip = React.forwardRef<unknown, TooltipProps>(
  function DismissibleTooltip({ children, ...props }, ref) {
    const [open, setOpen] = useState(false)

    // Inject onClickCapture directly onto the child so Ant Design can still
    // use the child's DOM node for positioning (a wrapper span breaks it).
    // Also forward the ref so parent Dropdown/Popconfirm can locate the DOM node.
    type WithHandlers = { onClickCapture?: React.MouseEventHandler; ref?: React.Ref<unknown> }
    const child = React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement<WithHandlers>, {
          onClickCapture: (e: React.MouseEvent) => {
            setOpen(false)
            ;(children as React.ReactElement<WithHandlers>).props.onClickCapture?.(e)
          },
          ref,
        })
      : children

    return (
      <Tooltip {...props} open={open} onOpenChange={setOpen}>
        {child}
      </Tooltip>
    )
  }
)
